import { TabularTypeSettings } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { IViolation, Metamodel, NamedArray } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { Office365Dataset } from 'app/model/dataset/office365.dataset';
import { office365UserScopes } from 'app/registries/consts/metadata-consts';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { BooleanDataFlowType, TimeStampDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { stringIsNullOrEmpty, format } from 'app/shared/utils/string.util';

export interface IMicrosoft365TableProperty {
    tableName: DataFlowExpression;
    autoFlatten: DataFlowExpression;
}

export interface IMicrosoft365ScopeProperty {
    userScope: (typeof office365UserScopes)[keyof (typeof office365UserScopes)];
    userScopeFilterUri: DataFlowExpression;
    allowedGroups: DataFlowExpressionArray;
}

export interface IMicrosoft365QueryProperty {
    dateFilterColumn: DataFlowExpression;
    startTime: DataFlowExpression;
    endTime: DataFlowExpression;
}

export interface IMicrosoft365StructureProperty {
    structure: DataFlowExpressionArray<DataFlowColumn>;
}

@Metamodel.model({
    name: adfModelTypeMap.office365.dataflowSettings,
    validator: Microsoft365Settings.validateSettings
})
export class Microsoft365Settings extends TabularTypeSettings implements IMicrosoft365TableProperty, IMicrosoft365ScopeProperty, IMicrosoft365QueryProperty, IMicrosoft365StructureProperty {
    public userScope: (typeof office365UserScopes)[keyof (typeof office365UserScopes)] = office365UserScopes.all;

    @Metamodel.attribute({
        display: stringResources.Office365_AutoFlatten
    })
    public autoFlatten: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.Office365_ScopeFilter_Label
    })
    public userScopeFilterUri: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.Office365_UserScope_AllowedGroups_Label
    })
    public allowedGroups: DataFlowExpressionArray;

    @Metamodel.attribute({
        display: stringResources.ColumnNameLabel
    })
    public dateFilterColumn: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.StartTimeUTC
    })
    public startTime: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.EndTimeUTC
    })
    public endTime: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.DataflowSchemaOutputColumnHeader
    })
    public structure: DataFlowExpressionArray<DataFlowColumn>;

    public getDatasetType(): string {
        return Metamodel.modelFor(Office365Dataset).name;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.Microsoft365;
    }

    public getIconClass(): string {
        return 'i-100x100-o365';
    }

    public getSubFormat(): string {
        return undefined;
    }

    public initialize(subFormat: string) {
        super.initialize(subFormat);
        this.autoFlatten = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.userScopeFilterUri = this.transform.createTemplatableExpression();
        this.allowedGroups = this.transform.createTemplatableExpressionArray();
        this.dateFilterColumn = this.transform.createTemplatableExpression();
        this.startTime = this.transform.createTemplatableExpression(TimeStampDataFlowType.TYPE);
        this.endTime = this.transform.createTemplatableExpression(TimeStampDataFlowType.TYPE);
        this.structure = this.transform.createTemplatableExpressionArray<DataFlowColumn>(new NamedArray<DataFlowColumn>());
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static validateSettings(settings: Microsoft365Settings): IViolation[] {
        const violations: IViolation[] = [];
        if (stringIsNullOrEmpty(settings.tableName?.expression)) {
            violations.push({
                context: settings.tableName,
                message: () => format(stringResources.RequiredTemplate, stringResources.Table_name_Label)
            });
        }
        if (!stringIsNullOrEmpty(settings.dateFilterColumn?.expression) && stringIsNullOrEmpty(settings.startTime?.expression)) {
            // startTime must exist if dateFilterColumn is defined
            violations.push({
                context: settings.startTime,
                message: () => stringResources.StartTimeValidation
            });
        }
        return violations.length > 0 ? violations : undefined;
    }
}
